package DataBase

import Network.NetworkOutput
import Network.httpWorker
import Structs.Api.ApiConfig
import Utils.getJsonWithConfig
import kotlinx.coroutines.delay
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

/**
 * Hosted payment sessions (pay/session create + status) — same contract as nexilcolab
 * payment-session.api.ts. Card data stays on the provider page.
 */
object PaymentSessionsClient {

    data class Session(
        val sessionId: String,
        val checkoutUrl: String,
        val amount: Double,
        val provider: String,
    )

    data class Status(
        val status: String,
        val transactionId: String?,
        val amount: Double,
        val last4: String?,
        val provider: String,
        val receiptState: String? = null,
        val receiptId: Int? = null,
        val receiptError: String? = null,
    )

    data class Result<T>(
        val data: T? = null,
        val ok: Boolean = false,
        val notConfigured: Boolean = false,
        val error: String? = null,
    )

    fun paymentLinkShareMessage(
        clientName: String,
        amountLabel: String,
        url: String,
        merchantName: String? = null,
    ): String {
        val merchant = merchantName?.trim().orEmpty()
        return if (merchant.isNotEmpty()) {
            "שלום $clientName,\nלתשלום מאובטח ל$merchant בסך $amountLabel:\n$url"
        } else {
            "שלום $clientName,\nלתשלום מאובטח בסך $amountLabel:\n$url"
        }
    }

    fun hasHostedPaymentConfig(config: ApiConfig? = UINKDBInterface.activeDB.apiConfig): Boolean {
        val raw = config?.credit ?: return false
        if (raw.isBlank() || raw == "{}") return false
        return try {
            val parsed = getJsonWithConfig().parseToJsonElement(raw)
            val obj = parsed as? JsonObject ?: return false
            val provider = obj["provider"]?.jsonPrimitive?.contentOrNull
            if (provider.equals("payplus", ignoreCase = true)) {
                val apiKey = obj["api_key"]?.jsonPrimitive?.contentOrNull
                val secretKey = obj["secret_key"]?.jsonPrimitive?.contentOrNull
                val pageUid = obj["payment_page_uid"]?.jsonPrimitive?.contentOrNull
                return !apiKey.isNullOrBlank() && !secretKey.isNullOrBlank() && !pageUid.isNullOrBlank()
            }
            val terminal = obj["terminal_name"]?.jsonPrimitive?.contentOrNull
            val appKey = obj["app_key"]?.jsonPrimitive?.contentOrNull
            val secret = obj["secret"]?.jsonPrimitive?.contentOrNull
            !terminal.isNullOrBlank() && !appKey.isNullOrBlank() && !secret.isNullOrBlank()
        } catch (_: Exception) {
            false
        }
    }

    suspend fun createSession(
        amount: Double,
        purpose: String? = null,
        ref: String? = null,
        clientId: Int? = null,
        /** Open invoice ids to settle; null/empty uses the client's FIFO queue. */
        invoiceIds: List<Int>? = null,
        contact: String? = null,
        company: String? = null,
        email: String? = null,
        phone: String? = null,
        address: String? = null,
        city: String? = null,
        method: String? = null,
        /** Hosted checkout installment cap (Tranzila ``maxpay``). */
        paymentPlan: Int? = null,
        /**
         * Stable key for one payment, reused across retries so the provider
         * can refuse to charge the same payment twice. Callers that retry
         * must pass the same value they used for the first attempt.
         */
        intentKey: String? = null,
    ): Result<Session> {
        if (!hasHostedPaymentConfig()) {
            // Flask `/clients/api/config` omits the credit column unless credit=true.
            // Callers that only asked for WhatsApp leave ApiConfig.credit as "{}".
            runCatching {
                UINKDBInterface.activeDB.getApiConfig(whatsapp = true, credit = true)
            }
            if (!hasHostedPaymentConfig()) {
                return Result(notConfigured = true, error = "אין הגדרת ספק תשלום")
            }
        }
        if (amount <= 0.0) return Result(error = "סכום חייב להיות גדול מ-0")
        if (method.equals("bit", ignoreCase = true) && amount < 5.0) {
            return Result(error = "סכום מינימום לחיוב ביט הוא 5 ש\"ח")
        }
        val body = mutableMapOf<String, kotlinx.serialization.json.JsonElement>(
            "company_id" to JsonPrimitive(httpWorker.companyId),
            "amount" to JsonPrimitive(amount),
        )
        purpose?.takeIf { it.isNotBlank() }?.let { body["purpose"] = JsonPrimitive(it) }
        ref?.takeIf { it.isNotBlank() }?.let { body["ref"] = JsonPrimitive(it) }
        clientId?.let { body["client_id"] = JsonPrimitive(it) }
        invoiceIds
            ?.filter { it > 0 }
            ?.distinct()
            ?.takeIf { it.isNotEmpty() }
            ?.let { ids -> body["invoice_ids"] = JsonArray(ids.map { JsonPrimitive(it) }) }
        contact?.takeIf { it.isNotBlank() }?.let { body["contact"] = JsonPrimitive(it) }
        company?.takeIf { it.isNotBlank() }?.let { body["company"] = JsonPrimitive(it) }
        email?.takeIf { it.isNotBlank() }?.let { body["email"] = JsonPrimitive(it) }
        phone?.takeIf { it.isNotBlank() }?.let { body["phone"] = JsonPrimitive(it) }
        address?.takeIf { it.isNotBlank() }?.let { body["address"] = JsonPrimitive(it) }
        city?.takeIf { it.isNotBlank() }?.let { body["city"] = JsonPrimitive(it) }
        method?.takeIf { it.isNotBlank() }?.let { body["method"] = JsonPrimitive(it) }
        paymentPlan?.takeIf { it > 0 }?.let { body["payment_plan"] = JsonPrimitive(it) }
        intentKey?.takeIf { it.isNotBlank() }?.let { body["intent_key"] = JsonPrimitive(it) }

        val res = httpWorker.purePostApi(
            url = "$server_ip2/pay/session/create",
            params = null,
            token = httpWorker.curToken,
            jsonStr = JsonObject(body).toString(),
        )
        if (res.second != NetworkOutput.SUCCESS || res.first.isNullOrBlank()) {
            return Result(error = extractError(res.first) ?: "יצירת סשן תשלום נכשלה")
        }
        return parsePayload(res.first!!) { obj ->
            val sessionId = obj["session_id"]?.jsonPrimitive?.contentOrNull.orEmpty()
            val checkout = obj["checkout_url"]?.jsonPrimitive?.contentOrNull
                ?: obj["iframe_url"]?.jsonPrimitive?.contentOrNull
                ?: ""
            val provider = obj["provider"]?.jsonPrimitive?.contentOrNull.orEmpty()
            val sum = obj["amount"]?.jsonPrimitive?.contentOrNull?.toDoubleOrNull()
                ?: obj["sum"]?.jsonPrimitive?.contentOrNull?.toDoubleOrNull()
                ?: amount
            if (sessionId.isBlank() || checkout.isBlank()) {
                Result(error = "תשובת ספק תשלום לא תקינה")
            } else {
                Result(
                    data = Session(sessionId, checkout, sum, provider),
                    ok = true,
                )
            }
        }
    }

    suspend fun getStatus(sessionId: String): Result<Status> {
        if (sessionId.isBlank()) return Result(error = "חסר מזהה סשן")
        val body = JsonObject(
            mapOf(
                "company_id" to JsonPrimitive(httpWorker.companyId),
                "session_id" to JsonPrimitive(sessionId),
            )
        ).toString()
        val res = httpWorker.purePostApi(
            url = "$server_ip2/pay/session/status",
            params = null,
            token = httpWorker.curToken,
            jsonStr = body,
        )
        if (res.second != NetworkOutput.SUCCESS || res.first.isNullOrBlank()) {
            return Result(error = extractError(res.first) ?: "בדיקת סטטוס נכשלה")
        }
        return parsePayload(res.first!!) { obj ->
            Result(
                data = Status(
                    status = obj["status"]?.jsonPrimitive?.contentOrNull.orEmpty(),
                    transactionId = obj["transaction_id"]?.jsonPrimitive?.contentOrNull,
                    amount = obj["amount"]?.jsonPrimitive?.contentOrNull?.toDoubleOrNull()
                        ?: obj["sum"]?.jsonPrimitive?.contentOrNull?.toDoubleOrNull()
                        ?: 0.0,
                    last4 = obj["last4"]?.jsonPrimitive?.contentOrNull,
                    provider = obj["provider"]?.jsonPrimitive?.contentOrNull.orEmpty(),
                    receiptState = obj["receipt_state"]?.jsonPrimitive?.contentOrNull,
                    receiptId = obj["receipt_id"]?.jsonPrimitive?.contentOrNull?.toIntOrNull(),
                    receiptError = obj["receipt_error"]?.jsonPrimitive?.contentOrNull,
                ),
                ok = true,
            )
        }
    }

    /**
     * Poll until paid / declined / timeout.
     *
     * Only ``failed`` is a final decline that a caller may offer to retry.
     * ``needs_review`` and a timeout both mean the card may have been charged
     * without confirmation, so they must never be presented as "try again".
     */
    suspend fun waitUntilPaid(
        sessionId: String,
        maxAttempts: Int = 80,
        delayMs: Long = 1500L,
    ): Result<Status> {
        repeat(maxAttempts) {
            val st = getStatus(sessionId)
            if (!st.ok || st.data == null) {
                // keep polling through transient errors
            } else when (st.data.status) {
                "paid" -> return st
                "failed" -> return Result(error = "הסליקה לא אושרה. ניתן לנסות שוב.", data = st.data)
                "needs_review" -> return Result(
                    error = "התקבל חיוב שלא ניתן לאמת מול ספק התשלום. " +
                        "אין לחייב שוב — יש לבדוק בטרנזילה אם הכרטיס חויב.",
                    data = st.data,
                )
            }
            delay(delayMs)
        }
        return Result(
            error = "לא התקבלה תשובה סופית מספק התשלום. ייתכן שהכרטיס חויב — " +
                "יש לבדוק בטרנזילה לפני ניסיון נוסף.",
        )
    }

    private fun extractError(raw: String?): String? {
        if (raw.isNullOrBlank()) return null
        return try {
            val root = getJsonWithConfig().parseToJsonElement(raw) as? JsonObject ?: return raw
            root["status_msg"]?.jsonPrimitive?.contentOrNull
                ?: root["msg"]?.jsonPrimitive?.contentOrNull
                ?: raw
        } catch (_: Exception) {
            raw
        }
    }

    private fun <T> parsePayload(raw: String, map: (JsonObject) -> Result<T>): Result<T> {
        return try {
            val root = getJsonWithConfig().parseToJsonElement(raw) as JsonObject
            val status = root["status"]?.jsonPrimitive?.contentOrNull
            if (status != null && status != "SUCCESS") {
                return Result(error = extractError(raw) ?: "שגיאה")
            }
            val msg = root["status_msg"]
            val obj = when (msg) {
                is JsonObject -> msg
                is JsonPrimitive -> {
                    val text = msg.contentOrNull ?: return Result(error = "תשובה לא תקינה")
                    getJsonWithConfig().parseToJsonElement(text) as? JsonObject
                        ?: return Result(error = "תשובה לא תקינה")
                }
                else -> return Result(error = "תשובה לא תקינה")
            }
            map(obj)
        } catch (e: Exception) {
            Result(error = e.message ?: "parse failed")
        }
    }
}
